import React, { useState, useEffect } from 'react';
import { getSupabaseClient } from '../../lib/supabase';
import { AddPaymentMethodModal } from './AddPaymentMethodModal';

interface PaymentMethod {
  id: string;
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
  isDefault: boolean;
}

export const PaymentMethodsList: React.FC = () => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    setLoading(true);
    setError(null);

    try {
      const supabase = getSupabaseClient();

      // Ensure customer exists
      await supabase.functions.invoke('stripe-ensure-customer', {
        method: 'POST',
      });

      // List payment methods
      const { data, error: listError } = await supabase.functions.invoke<{
        paymentMethods: PaymentMethod[];
      }>('stripe-list-payment-methods', {
        method: 'GET',
      });

      if (listError) {
        throw listError;
      }

      setPaymentMethods(data?.paymentMethods || []);
    } catch (err) {
      console.error('Failed to load payment methods:', err);
      setError(err instanceof Error ? err.message : 'Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    setProcessingId(paymentMethodId);
    setError(null);

    try {
      const supabase = getSupabaseClient();
      const { error: setError } = await supabase.functions.invoke('stripe-set-default-payment-method', {
        method: 'POST',
        body: { paymentMethodId },
      });

      if (setError) {
        throw setError;
      }

      await loadPaymentMethods();
    } catch (err) {
      console.error('Failed to set default payment method:', err);
      setError(err instanceof Error ? err.message : 'Failed to set default payment method');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRemove = async (paymentMethodId: string) => {
    const pm = paymentMethods.find((p) => p.id === paymentMethodId);
    if (pm?.isDefault) {
      setError('Cannot remove default payment method. Set another card as default first.');
      return;
    }

    if (!confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    setProcessingId(paymentMethodId);
    setError(null);

    try {
      const supabase = getSupabaseClient();
      const { error: detachError } = await supabase.functions.invoke('stripe-detach-payment-method', {
        method: 'POST',
        body: { paymentMethodId },
      });

      if (detachError) {
        throw detachError;
      }

      await loadPaymentMethods();
    } catch (err) {
      console.error('Failed to remove payment method:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove payment method');
    } finally {
      setProcessingId(null);
    }
  };

  const getCardBrandIcon = (brand?: string) => {
    const brandLower = brand?.toLowerCase();
    switch (brandLower) {
      case 'visa':
        return '💳';
      case 'mastercard':
        return '💳';
      case 'amex':
        return '💳';
      case 'discover':
        return '💳';
      default:
        return '💳';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#2C2731]" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 rounded-2xl border border-red-300 bg-[#FBE2E2] px-4 py-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[#2C2731]">Saved Payment Methods</h2>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="rounded-lg border border-[#2C2731] bg-[#FEF6D6] px-4 py-2 text-sm font-semibold text-[#2C2731] transition hover:bg-[#E8DFBD]"
        >
          Add Card
        </button>
      </div>

      {paymentMethods.length === 0 ? (
        <div className="rounded-2xl border border-[#2C2731] bg-[#FEF6D6] py-12 text-center shadow-sm">
          <svg
            className="mx-auto mb-3 h-12 w-12 text-[#2C2731]/50"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
          <p className="mb-4 text-sm text-[#2C2731]/70">No payment methods saved</p>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="rounded-lg bg-[#2C2731] px-4 py-2 text-sm font-semibold text-[#FEF6D6] transition hover:bg-[#1F1924]"
          >
            Add Your First Card
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {paymentMethods.map((pm) => (
            <div
              key={pm.id}
              className="flex items-center justify-between rounded-2xl border border-[#2C2731]/40 bg-[#FEF6D6] p-4 shadow-sm"
            >
              <div className="flex items-center space-x-4">
                <span className="text-2xl">{getCardBrandIcon(pm.brand)}</span>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-[#2C2731]">
                      {pm.brand?.charAt(0).toUpperCase()}
                      {pm.brand?.slice(1)} •••• {pm.last4}
                    </p>
                    {pm.isDefault && (
                      <span className="rounded-full bg-[#2C2731] px-2 py-0.5 text-xs font-semibold text-[#FEF6D6]">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#2C2731]/70">
                    Expires {pm.expMonth?.toString().padStart(2, '0')}/{pm.expYear?.toString().slice(-2)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {!pm.isDefault && (
                  <button
                    onClick={() => handleSetDefault(pm.id)}
                    disabled={processingId === pm.id}
                    className="rounded-lg border border-[#2C2731] px-3 py-1.5 text-sm font-semibold text-[#2C2731] transition hover:bg-[#E8DFBD] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {processingId === pm.id ? 'Setting...' : 'Set Default'}
                  </button>
                )}
                <button
                  onClick={() => handleRemove(pm.id)}
                  disabled={pm.isDefault || processingId === pm.id}
                  className="rounded-lg border border-transparent px-3 py-1.5 text-sm font-semibold text-red-600 transition hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  title={pm.isDefault ? 'Set another card as default first' : 'Remove card'}
                >
                  {processingId === pm.id ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddPaymentMethodModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdded={loadPaymentMethods}
      />
    </div>
  );
};
